import { AppShell, Burger, Button, CloseButton, Group, Modal, Text } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  type MouseEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { useTranslation } from 'react-i18next';
import classes from './AppLayout.module.css';
import type { MenuItem, MenuScope } from './menu.js';
import { type Brand, SideNav } from './SideNav.js';
import { createNavigationGuard, ShellContext } from './shell-context.js';
import { type SearchGroup, TopBar } from './TopBar.js';
import { LAYERS, LAYOUT } from './tokens.js';
import { VesselBadge } from './VesselBadge.js';

export interface AppLayoutProps {
  scope: MenuScope;
  /** Geçerli yol (`location.pathname`); aktif menü öğesi ve breadcrumb buradan. */
  path: string;
  /** Konum sorgusu (`location.search`, ör. `?user=…`); ekranlar `useShell().search` ile okur. */
  search?: string | undefined;
  onNavigate: (path: string) => void;
  /** Şirket markası; yoksa Felixmate (ADR 0019 §4). */
  brand?: Brand | null;
  /** Sol alttaki rozet: gemide gemi adı, ofiste slogan. */
  badge: { title: string; subtitle: string };
  /** Sürüm satırı (NR496 [4.1.2]); dar menüde `compact` sürümü verilir. */
  version: (compact: boolean) => ReactNode;
  /** `detail`: rol ya da rütbe; `email` yalnız kullanıcı menüsünün başlığında. */
  user: { name: string; detail: string; email?: string | undefined };
  userMenu: ReactNode;
  notifications: { count: number; content: ReactNode };
  searchGroups?: readonly SearchGroup[] | undefined;
  /** Yetkiye göre gizlenen menü öğeleri. */
  hiddenMenuItems?: ReadonlySet<string> | undefined;
  /** Üst barın hemen altındaki şerit (güncelleme, bağlantı uyarısı); içerikle kaymaz. */
  banner?: ReactNode;
  /**
   * Yalnız masaüstü: içerik kaydırma kabı `main` olur (üst barın altında başlar), böylece kaydırma
   * çubuğu beyaz üst barın yanında görünmez. Web'de belge kaydırması sürer (tam sayfa görüntüler).
   * Varsayılan: gemi uygulamasında açık, web ofiste kapalı (M6f G1-18).
   */
  scrollMain?: boolean;
  /** Menü; varsayılan müşteri menüsü (`MENU`). Yönetici paneli kendi menüsünü verir (ADR 0039 §14). */
  menu?: readonly MenuItem[] | undefined;
  /** Breadcrumb'ın ev yolu; varsayılan `/dashboard`. */
  homePath?: string | undefined;
  /** Üst bar aramasının yer tutucusu; varsayılan müşteri uygulamasınınki. */
  searchPlaceholder?: string | undefined;
  children: ReactNode;
}

/** Mantine pencereleri Escape'te bu işaretli öğeden gelen tuşu yok sayar. */
const STOP_ESCAPE = { 'data-mantine-stop-propagation': 'true' } as const;

/**
 * Ortak düzen (ADR 0019; docs/ekranlar/menu-yapisi.md): sol menü tam yükseklikte, üst bar onun
 * sağında. ≥1280 px tam menü; 1024–1279 px yalnız ikonlar; <1024 px menü çekmecede
 * (docs/ekranlar/README.md "Responsive kurallar").
 */
export function AppLayout({
  scope,
  path,
  onNavigate,
  brand,
  badge,
  version,
  user,
  userMenu,
  notifications,
  searchGroups,
  hiddenMenuItems,
  search = '',
  banner,
  scrollMain = scope === 'ship',
  menu,
  homePath,
  searchPlaceholder,
  children,
}: AppLayoutProps) {
  const { t } = useTranslation();
  const [opened, { toggle, close }] = useDisclosure(false);
  const compact = useMediaQuery('(min-width: 1024px) and (max-width: 1279px)') ?? false;
  const width = compact ? LAYOUT.navbarWidthCompact : LAYOUT.navbarWidth;

  // Kirli formda kabuk üzerinden gezinme onay ister (M6f G1-11)
  const guard = useMemo(() => createNavigationGuard(), []);
  const confirming = useSyncExternalStore(guard.subscribe, guard.hasPending, guard.hasPending);
  // Onay açıkken Escape "Stay" ile aynı: yalnız onay kapanır, alttaki pencere ve form kalır
  useEffect(() => {
    if (!confirming) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.isComposing) guard.cancel();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [confirming, guard]);
  const navigate = (target: string) =>
    guard.request(() => {
      close();
      onNavigate(target);
    });
  // Telefonda menü çekmecesi Escape ile ve yol değişince (arama, geri tuşu) kapanır
  useEffect(() => {
    if (!opened) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.isComposing) close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [opened, close]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: yalnız yol değişince
  useEffect(() => close(), [path, close]);
  const context = useMemo(
    () => ({
      scope,
      path,
      search,
      guard,
      navigate: (target: string) => guard.request(() => onNavigate(target)),
      menu,
      homePath,
    }),
    [scope, path, search, guard, onNavigate, menu, homePath],
  );
  const shownBrand: Brand = brand ?? {
    name: 'Felixmate',
    tagline: t('shell.defaultTagline'),
    logo: null,
    product: true,
  };

  // Yol değişince odak sayfanın başlığına (h1) gider: ekran okuyucu yeni sayfayı okur, Tab içerikten
  // sürer (M6f G1-23). İçerikte zaten odak varsa (otomatik odaklı alan) dokunulmaz.
  const mainRef = useRef<HTMLElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: yalnız yol değişince
  useEffect(() => {
    const main = mainRef.current;
    const active = document.activeElement;
    if (!main || (active && active !== document.body && main.contains(active))) return;
    if (active?.closest('[role="dialog"]')) return;
    main.querySelector<HTMLElement>('h1')?.focus({ preventScroll: true });
  }, [path]);

  const skipToContent = (event: MouseEvent<HTMLAnchorElement>) => {
    // Uygulama yönlendiricisi adresi değiştirmesin: odak doğrudan içeriğe
    event.preventDefault();
    const main = mainRef.current;
    (main?.querySelector<HTMLElement>('h1') ?? main)?.focus();
  };

  return (
    <ShellContext.Provider value={context}>
      {/* Klavye: görsel olarak gizli, odakta görünen "Skip to content" (M6f G1-23) */}
      {/* biome-ignore lint/a11y/useValidAnchor: atlama bağlantısı; uygulama yönlendiricisi adresi değiştirmesin diye odak elle taşınır */}
      <a href="#content" className={classes.skip} onClick={skipToContent}>
        {t('shell.skipToContent')}
      </a>
      <AppShell
        layout="alt"
        header={{ height: LAYOUT.headerHeight }}
        navbar={{ width, breakpoint: 1024, collapsed: { mobile: !opened } }}
        padding={0}
        className={classes.shell}
        data-scope={scope}
      >
        <AppShell.Header className={classes.header}>
          <TopBar
            onNavigate={navigate}
            searchGroups={searchGroups}
            menu={menu}
            searchPlaceholder={searchPlaceholder}
            notifications={notifications}
            user={user}
            userMenu={userMenu}
            burger={
              <Burger
                opened={opened}
                onClick={toggle}
                size="sm"
                aria-label={t('shell.toggleMenu')}
                className={classes.burger}
              />
            }
          />
        </AppShell.Header>

        {/* Telefonda (<1024 px) açık menünün arkası karartılır; dokununca menü kapanır. Menü üst
            barın burger düğmesini örttüğü için kapatma düğmesi menünün kendisinde (2026-10-11). */}
        {opened ? <div className={classes.backdrop} onClick={close} aria-hidden /> : null}
        <AppShell.Navbar className={classes.navbar} data-opened={opened || undefined}>
          <CloseButton
            onClick={close}
            size="lg"
            variant="transparent"
            aria-label={t('shell.closeMenu')}
            className={classes.closeMenu}
          />
          <SideNav
            path={path}
            onNavigate={navigate}
            brand={shownBrand}
            compact={compact}
            hidden={hiddenMenuItems}
            menu={menu}
            footer={
              <VesselBadge
                title={badge.title}
                subtitle={badge.subtitle}
                version={version(compact)}
                compact={compact}
              />
            }
          />
        </AppShell.Navbar>

        <AppShell.Main
          ref={mainRef}
          id="content"
          tabIndex={-1}
          className={classes.main}
          data-scroll-main={scrollMain || undefined}
        >
          {banner ? <div className={classes.banner}>{banner}</div> : null}
          <div className={classes.content}>{children}</div>
        </AppShell.Main>
      </AppShell>

      {/* Onay her pencerenin ve açılır listenin üstünde (LAYERS.confirm; M6f G1d-2). Escape'i
          kabuk karşılar: öğeleri `data-mantine-stop-propagation` taşır, böylece Mantine'in pencere
          başına Escape dinleyicisi alttaki düzenleme penceresini kapatıp formu silmez. */}
      <Modal
        opened={confirming}
        onClose={guard.cancel}
        title={t('common.unsaved.title')}
        size="sm"
        zIndex={LAYERS.confirm}
        closeOnEscape={false}
        attributes={{ content: STOP_ESCAPE, close: STOP_ESCAPE }}
      >
        <Text fz="sm" c="dimmed">
          {t('common.unsaved.message')}
        </Text>
        <Group justify="flex-end" gap="sm" mt="lg">
          <Button variant="default" onClick={guard.cancel} data-autofocus {...STOP_ESCAPE}>
            {t('common.unsaved.stay')}
          </Button>
          <Button color="red" onClick={guard.confirm} {...STOP_ESCAPE}>
            {t('common.unsaved.discard')}
          </Button>
        </Group>
      </Modal>
    </ShellContext.Provider>
  );
}
